// Admin permission system for Daniel Albert Law Firm
// Roles: superadmin > admin > editor > user

export type AdminRole = 'superadmin' | 'admin' | 'editor' | 'user';

export type AdminSection = 
  | 'dashboard'
  | 'appointments'
  | 'blog'
  | 'pages'
  | 'payments'
  | 'integrations'
  | 'settings'
  | 'users';

// Permission matrix - defines what each role can access
const permissionMatrix: Record<AdminRole, AdminSection[]> = {
  superadmin: [
    'dashboard',
    'appointments',
    'blog',
    'pages',
    'payments',
    'integrations',
    'settings',
    'users',
  ],
  admin: [
    'dashboard',
    'appointments',
    'blog',
    'pages',
    'payments',
    'integrations',
    'settings',
    // Note: admin cannot manage users (only superadmin can)
  ],
  editor: [
    'dashboard',
    'blog',
    'pages',
    // Note: editor cannot access payments or integrations
  ],
  user: [
    // Regular users have no admin access
  ],
};

// Role hierarchy - higher number = higher privilege
const roleHierarchy: Record<AdminRole, number> = {
  superadmin: 4,
  admin: 3,
  editor: 2,
  user: 1,
};

/**
 * Check if a user with given role has access to a specific section
 */
export const hasPermission = (role: AdminRole | null, section: AdminSection): boolean => {
  if (!role) return false;
  return permissionMatrix[role]?.includes(section) ?? false;
};

/**
 * Check if a user role is admin-level (can access admin panel at all)
 */
export const isAdminRole = (role: AdminRole | null): boolean => {
  if (!role) return false;
  return roleHierarchy[role] >= roleHierarchy.editor;
};

/**
 * Get all accessible sections for a role
 */
export const getAccessibleSections = (role: AdminRole | null): AdminSection[] => {
  if (!role) return [];
  return permissionMatrix[role] ?? [];
};

/**
 * Compare two roles and return if first has higher or equal privilege
 */
export const hasHigherOrEqualRole = (userRole: AdminRole | null, requiredRole: AdminRole): boolean => {
  if (!userRole) return false;
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: AdminRole | null): string => {
  if (!role) return 'Unknown';
  const names: Record<AdminRole, string> = {
    superadmin: 'Super Admin',
    admin: 'Administrator',
    editor: 'Editor',
    user: 'User',
  };
  return names[role];
};

/**
 * Navigation items for admin panel with permission requirements
 */
export const adminNavItems = [
  {
    title: 'Dashboard',
    href: '/admin/dashboard',
    icon: 'LayoutDashboard',
    section: 'dashboard' as AdminSection,
  },
  {
    title: 'Appointments',
    href: '/admin/appointments',
    icon: 'Calendar',
    section: 'appointments' as AdminSection,
  },
  {
    title: 'Blog',
    href: '/admin/blog',
    icon: 'FileText',
    section: 'blog' as AdminSection,
  },
  {
    title: 'Pages',
    href: '/admin/pages',
    icon: 'Layout',
    section: 'pages' as AdminSection,
  },
  {
    title: 'Payments',
    href: '/admin/payments',
    icon: 'CreditCard',
    section: 'payments' as AdminSection,
  },
  {
    title: 'Integrations',
    href: '/admin/integrations',
    icon: 'Plug',
    section: 'integrations' as AdminSection,
  },
  {
    title: 'Settings',
    href: '/admin/settings',
    icon: 'Settings',
    section: 'settings' as AdminSection,
  },
];
